import React from 'react';
import { useIntl } from 'react-intl';

import { FilterableSelect, Option } from 'carbon-react/lib/components/select';

import { ReportTemplateList } from '@futrli-sdk/predict';

import { EmptySelect } from '../../empty-select/empty-select';
import { REPORT_FORM_FIELD_IDS } from '../constants';
import { CreateReportSidebarMode } from '../create-report-sidebar';

export interface WorkspaceTemplateSelectProps {
  templates: ReportTemplateList[];
  selectedTemplateId: string;
  onSelectedTemplateIdChange: (value: string) => void;
  onNavigateToCreateTemplate: () => void;
  error?: string;
  onBlur?: () => void;
  mode?: CreateReportSidebarMode;
}

export const WorkspaceTemplateSelect = ({
  templates,
  selectedTemplateId,
  onSelectedTemplateIdChange,
  onNavigateToCreateTemplate,
  error,
  onBlur,
  mode = 'report'
}: WorkspaceTemplateSelectProps) => {
  const intl = useIntl();
  const isDashboard = mode === 'dashboard';

  if (templates.length > 0) {
    return (
      <FilterableSelect
        id={REPORT_FORM_FIELD_IDS.WORKSPACE_TEMPLATE_SELECT}
        name={REPORT_FORM_FIELD_IDS.WORKSPACE_TEMPLATE_SELECT}
        label={intl.formatMessage({
          id: 'reporting.select_a_template',
          defaultMessage: 'Select a template'
        })}
        placeholder={intl.formatMessage({
          id: 'reporting.type_to_search_templates',
          defaultMessage: 'Type to search templates'
        })}
        inputHint={intl.formatMessage({
          id: 'reporting.type_to_search_published_templates',
          defaultMessage: 'Type to search published templates'
        })}
        value={selectedTemplateId}
        onChange={e => onSelectedTemplateIdChange(e.target.value as string)}
        mb={0}
        listMaxHeight={340}
        error={error}
        onBlur={onBlur}
        required
      >
        {templates.map(t => (
          <Option key={t.id} value={t.id} text={t.name} />
        ))}
      </FilterableSelect>
    );
  }

  return (
    <EmptySelect
      id={REPORT_FORM_FIELD_IDS.WORKSPACE_TEMPLATE_SELECT_EMPTY}
      label={intl.formatMessage({
        id: 'reporting.select_a_template',
        defaultMessage: 'Select a template'
      })}
      placeholder={intl.formatMessage({
        id: 'reporting.select_one',
        defaultMessage: 'Select one'
      })}
      inputHint={intl.formatMessage({
        id: 'reporting.type_to_search_published_templates',
        defaultMessage: 'Type to search published templates'
      })}
      emptyText={intl.formatMessage({
        id: 'reporting.templates.no_templates_available',
        defaultMessage: 'No templates available'
      })}
      createText={
        isDashboard
          ? intl.formatMessage({
              id: 'reporting.templates.create_a_dashboard_template',
              defaultMessage: 'Create a dashboard template'
            })
          : intl.formatMessage({
              id: 'reporting.templates.create_a_report_template',
              defaultMessage: 'Create a report template'
            })
      }
      onCreateAction={onNavigateToCreateTemplate}
      error={error}
    />
  );
};
